import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuth } from '../../features/auth/AuthContext'

export type ThemeMode = 'System' | 'Light' | 'Dark'
type ResolvedTheme = 'light' | 'dark'

type ThemeContextValue = {
  themeMode: ThemeMode
  resolvedTheme: ResolvedTheme
  setThemeMode: (themeMode: ThemeMode) => Promise<void>
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const storageKey = 'apl-enterprise.theme'

function storedTheme(): ThemeMode {
  const value = localStorage.getItem(storageKey)
  return value === 'Light' || value === 'Dark' ? value : 'System'
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'Light') return 'light'
  if (mode === 'Dark') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const { user, selectTheme } = useAuth()
  const [themeMode, updateThemeMode] = useState<ThemeMode>(storedTheme)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(storedTheme()))

  useEffect(() => {
    if (!user) return
    localStorage.setItem(storageKey, user.themeMode)
    updateThemeMode(user.themeMode)
  }, [user])

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const resolved = resolveTheme(themeMode)
      document.documentElement.dataset.theme = resolved
      document.documentElement.style.colorScheme = resolved
      document.querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', resolved === 'dark' ? '#07182e' : '#f4f7fb')
      setResolvedTheme(resolved)
    }
    apply()
    if (themeMode === 'System') media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [themeMode])

  const setThemeMode = useCallback(async (next: ThemeMode) => {
    if (user) await selectTheme(next)
    localStorage.setItem(storageKey, next)
    updateThemeMode(next)
  }, [selectTheme, user])

  const value = useMemo(() => ({ themeMode, resolvedTheme, setThemeMode }),
    [resolvedTheme, setThemeMode, themeMode])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside ThemeProvider')
  return context
}
